// src/components/IdentityChat/PublicChat.js
import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, useParams } from "react-router-dom";
import { db } from "../../firebase/config";
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  addDoc,
  serverTimestamp,
  doc,
  getDoc,
  updateDoc,
  getDocs,
  limit,
  where,
  collectionGroup,
  increment,
  setDoc,
} from "firebase/firestore";
import { generateAIMessage } from "../../services/aiService";
// import InternalChat from "./InternalChat";
import { getAuth, GoogleAuthProvider, signInWithPopup } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { arrayUnion, arrayRemove} from "firebase/firestore";
import { useMemo } from "react";
import { Button, Modal } from "react-bootstrap";
import { generateUsername } from "../../utils/usernameGenerator";

const auth = getAuth();

export default function PublicChat() {
  const { conversationId } = useParams();
  const [searchParams] = useSearchParams();

  // ✅ URL is the single source of truth
  const groupId = searchParams.get("group");        // "A", "B", or ""
  // const groupName = searchParams.get("groupName");  // actual name

  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [internalMessages, setInternalMessages] = useState([]);
  const [text, setText] = useState("");
  const messagesEndRef = useRef(null);
  const [aiState, setAiState] = useState(null);
  const navigate = useNavigate();
  const [countdown, setCountdown] = useState(null);
  const [profileUser, setProfileUser] = useState(null);
  const [startStep, setStartStep] = useState("invite");
  const [joinStep, setJoinStep] = useState("group"); 
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [username, setUsername] = useState("");
  // const [showUsernameModal, setShowUsernameModal] = useState(false);
  const [userphotoURL, setUserPhotoURL] = useState("")
  const [showInternalInfoModal, setShowInternalInfoModal] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [showInternalChats, setShowInternalChats] = useState(false);

  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showStartModal, setShowStartModal] = useState(false);
  const [showJoinModal, setShowJoinModal] = useState(false);


  const provider = new GoogleAuthProvider();

  // ✅ Fetch single conversation properly (not entire collection)
  useEffect(() => {
    if (!conversationId) return <p>Invalid chat link</p>;

    const unsubscribe = onSnapshot(
      doc(db, "conversations", conversationId),
      (snap) => {
        if (snap.exists()) {
          const data = snap.data();
          setConversation(data);
          setAiState(data);
        }
      }
    );

    return () => unsubscribe();
  }, [conversationId]);

  // ✅ Fetch public messages
  useEffect(() => {
    if (!conversationId) return;

    const q = query(
      collection(db, "conversations", conversationId, "messages"),
      orderBy("createdAt"),
      limit(100)
    );

    const unsubscribe = onSnapshot(q, snapshot => {
      setMessages(snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })));
    });

    return () => unsubscribe();
  }, [conversationId]);

  useEffect(() => {
  const user = auth.currentUser;
  if (!user || messages.length === 0) return;

  const markRead = async () => {
    const latest = messages[messages.length - 1];

    if (!latest || latest.type !== "official") return;

    const ref = doc(
      db,
      "conversations",
      conversationId,
      "messages",
      latest.id
    );

    if (!latest.readBy?.includes(user.uid)) {
      await updateDoc(ref, {
        readBy: arrayUnion(user.uid)
      });
    }

    // store last read message
    const readRef = doc(
      db,
      "userReads",
      `${user.uid}_${conversationId}`
    );

    await setDoc(readRef, {
      userId: user.uid,
      conversationId,
      lastReadMessageId: latest.id,
      readAt: serverTimestamp()
    });
  };

  markRead();
}, [messages]);

  // ✅ Fetch internal messages (only if in group A or B)
  useEffect(() => {
    if (!conversationId || !groupId || groupId === "") return;

    const q = query(
      collection(db, "conversations", conversationId, "internalMessages"),
      orderBy("createdAt")
    );

    const unsubscribe = onSnapshot(q, snapshot => {
      setInternalMessages(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });

    return () => unsubscribe();
  }, [conversationId, groupId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, internalMessages]);

  useEffect(() => {
  if (!conversation?.lastOfficialAt?.seconds) return;

  const updateCountdown = () => {
    const now = Date.now();
    const last = conversation.lastOfficialAt.seconds * 1000;

    const nextMessageTime = last + 5 * 60 * 1000;

    const diff = nextMessageTime - now;

    if (diff <= 0) {
      setCountdown("00:00");
      return;
    }

    const minutes = Math.floor(diff / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);

    const formatted =
      String(minutes).padStart(2, "0") +
      ":" +
      String(seconds).padStart(2, "0");

    setCountdown(formatted);
  };

  updateCountdown();

  const interval = setInterval(updateCountdown, 1000);

  return () => clearInterval(interval);
}, [conversation?.lastOfficialAt]);


// show join modal to unregistered users
useEffect(() => {
  if (!auth.currentUser) {
    setShowJoinModal(true);
  }
}, [groupId]);

// detecting first entry
useEffect(() => {
  const checkCreatorFirstEntry = async () => {
    const user = auth.currentUser;
    if (!user || !conversation) return;

    
    if (conversation.creatorId === user.uid && !conversation.hasStarted) {
      setShowStartModal(true);
      setShowInternalInfoModal(false);
    }

  };

  checkCreatorFirstEntry();
}, [conversation]);

useEffect(() => {
  const hidden = localStorage.getItem("hideInternalChatInfo");

  if (!hidden) {
    setShowInternalInfoModal(true);
  }
}, []);

useEffect(() => {
  const verifyGroupAccess = async () => {
    const user = auth.currentUser;
    if (!user || !conversationId) return;

    try {
      const ref = doc(
        db,
        "userConversations",
        `${user.uid}_${conversationId}`
      );

      const snap = await getDoc(ref);

      if (!snap.exists()) return;

      const savedGroup = snap.data().group;

      // 🚨 If user changed URL manually
      if (groupId && savedGroup !== groupId) {
        alert("You cannot switch groups. Access denied.");

        // 🔁 force correct group back
        navigate(`/chat/${conversationId}?group=${savedGroup}`, {
          replace: true
        });
      }

    } catch (err) {
      console.error("Group verification error:", err);
    }
  };

  verifyGroupAccess();
}, [groupId, conversationId]);

const combinedMessages = useMemo(() => {
  return [...messages, ...internalMessages]
    .sort((a, b) => (a.createdAt?.seconds || 0) - (b.createdAt?.seconds || 0))
    .filter(m => {
      if (m.type === "internal") {
        if (!showInternalChats) return false; // 🔥 hide all internal chats
        return m.fromGroup === groupId;
      }
      return true;
    });
}, [messages, internalMessages, groupId, showInternalChats]);

const sendMessage = async () => {
  if (!text || !groupId) return;

  try {
    
    const user = auth.currentUser;

    const userDoc = await getDoc(doc(db, "users", user.uid));
    const userData = userDoc.data();
    setUserPhotoURL(userData.photoURL)

    const convoRef = doc(db, "conversations", conversationId);
    const convoSnap = await getDoc(convoRef);
    const convoData = convoSnap.data();

    const { currentTurn } = convoData;

    // ❌ not your turn
    if (currentTurn && currentTurn !== groupId) return;

    // save internal message
    await addDoc(
      collection(db, "conversations", conversationId, "internalMessages"),
      {
        content: text,
        fromGroup: groupId,
        type: "internal",
        senderId: user.uid,
        senderName: userData?.username || user?.displayName,
        senderPhoto: userData.photoURL  || user?.photoURL,
        senderEmail: user?.email || null,
        votes: [],
        createdAt: serverTimestamp(),
      }
    );

    setText("");
    setShowInternalChats(true);


  } catch (err) {
    console.error("Error sending message:", err);
  }
};

  const formatTime = (timestamp) => {
    if (!timestamp?.seconds) return "";
    const date = new Date(timestamp.seconds * 1000);
    return date.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (!conversation) return <p>Loading chat...</p>;
  
  const isMyTurn =
  conversation?.currentTurn === null ||
  conversation?.currentTurn === groupId;

  // 🔎 Get last official AI message
  const lastOfficialMessage = [...messages]
    .reverse()
    .find(m => m.type === "official");

  const respondingToGroupName =
    lastOfficialMessage?.fromGroup === "A"
      ? conversation?.groupA?.name
      : conversation?.groupB?.name;
      

  const voteInternalMessage = async (message) => {
    const user = auth.currentUser;
    if (!user) return;

    const ref = doc(
      db,
      "conversations",
      conversationId,
      "internalMessages",
      message.id
    );

    const alreadyVoted = message.votes?.includes(user.uid);

    await updateDoc(ref, {
      votes: alreadyVoted
        ? arrayRemove(user.uid)
        : arrayUnion(user.uid)
    });

    // 🔹 Update reputation of message author
    if (message.senderId) {
      const userRef = doc(db, "users", message.senderId);

      await updateDoc(userRef, {
        "stats.totalVotesReceived": increment(alreadyVoted ? -1 : 1)
      });
    }
  };



  const openProfile = async (userId) => {
    try {
      if (!userId) return;

      const userDoc = await getDoc(doc(db, "users", userId));

      if (!userDoc.exists()) return;

      const userData = userDoc.data();
      setUserPhotoURL(userData.photoURL)
      // console.log("User data:", userData);s

      setProfileUser({
        name: userData.username,
        photo: userData.photoURL,
        groupsJoined: userData?.stats?.groupsJoined || 0,
        totalVotes: userData?.stats?.totalVotesReceived || 0,
        officialMessages: userData?.stats?.officialMessages || 0,
        groups: userData?.groups || [],
        groupsStarted: userData?.stats?.groupsStarted || 0,
      });

      setShowProfileModal(true);

    } catch (err) {
      console.error("Profile error:", err);
    }
  };


  const shareChat = async () => {


    const link = `${window.location.origin}/chat/${conversationId}`;

    try {
      if (navigator.share) {
        await navigator.share({
          title: "Channel of communication is now open between groups.",
          text: conversation.groupA?.name + " vs " + conversation.groupB?.name + ". \n \n " +  "Topic: " + conversation.topic + ". \n \n " ,
          url: link
        });
      } else {
        await navigator.clipboard.writeText(link);
      }

      // move to next step
      setStartStep("message");
      // setShowProfileModal(true);
      setShowInternalChats(true)

    } catch (err) {
      console.log("Share cancelled");
    }
  };


  const sendKickstartMessage = async () => {
  if (!text || !groupId) return;

  try {
    const user = auth.currentUser;

    const convoRef = doc(db, "conversations", conversationId);
    const convoSnap = await getDoc(convoRef);
    const convoData = convoSnap.data();

    const { currentTurn } = convoData;

    // ❌ not your turn
    if (currentTurn && currentTurn !== groupId) return;

    // save internal message (same as sendMessage)
    await addDoc(
      collection(db, "conversations", conversationId, "internalMessages"),
      {
        content: text,
        fromGroup: groupId,
        type: "internal",
        senderId: user.uid,
        senderName: user?.displayName || "Unknown",
        senderPhoto: user?.photoURL || null,
        senderEmail: user?.email || null,
        votes: [],
        createdAt: serverTimestamp(),
      }
    );

    // 🔥 mark conversation as started
    await updateDoc(convoRef, {
      hasStarted: true
    });
    
    // clear input
    setText("");
    // close modal
    setShowStartModal(false);

  } catch (err) {
    console.error("Error sending kickstart message:", err);
  }
};

const closeInternalInfoModal = () => {
  if (dontShowAgain) {
    localStorage.setItem("hideInternalChatInfo", "true");
  }

  setShowInternalInfoModal(false);
};

   
  
  return (
  <div className="d-flex flex-column vh-100">

{/* Modern Chat Header */}
<div
  style={{
    position: "sticky",
    top: 0,
    zIndex: 10,
    background: "rgba(255,255,255,0.85)",
    backdropFilter: "blur(10px)",
    borderBottom: "1px solid rgba(0,0,0,0.05)",
    padding: "12px 18px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  }}
>

  {/* LEFT SIDE */}
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: "12px"
    }}
  >

    {/* Back Button */}
    <button
      onClick={() => navigate("/")}
      style={{
        border: "none",
        background: "transparent",
        width: "36px",
        height: "36px",
        borderRadius: "12px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        transition: "all 0.2s ease",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = "rgba(0,0,0,0.05)";
        e.currentTarget.style.transform = "scale(1.05)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "transparent";
        e.currentTarget.style.transform = "scale(1)";
      }}
    >
      <span
        style={{
          fontSize: "30px",
          fontWeight: 600,
          color: "#111",
          lineHeight: 1,
        }}
      >
        ←
      </span>
    </button>

    {/* Group Avatars + Names */}
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "10px",
      }}
    >

      {/* Overlapping Avatars */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          position: "relative",
          height: "36px",
        }}
      >

        {/* Group A */}
        <div
          style={{
            width: "36px",
            height: "36px",
            borderRadius: "50%",
            background: "#0d6efd",
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: 600,
            fontSize: "14px",
            border: "2px solid white",
            zIndex: 2,
          }}
        >
          {conversation?.groupA?.name?.charAt(0)?.toUpperCase()}
        </div>

        {/* Group B */}
        <div
          style={{
            width: "36px",
            height: "36px",
            borderRadius: "50%",
            background: "#6f42c1",
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: 600,
            fontSize: "14px",
            border: "2px solid white",
            marginLeft: "-12px",
          }}
        >
          {conversation?.groupB?.name?.charAt(0)?.toUpperCase()}
        </div>

      </div>

      {/* Group Names */}
      <span
        style={{
          fontWeight: 400,
          fontSize: "14px",
          color: "#1f2937",
        }}
      >
        {isMyTurn ? (
            <>
               {conversation?.groupA?.name} <em>replying</em> {conversation?.groupB?.name}

              </>
            ) : (
              <>
               {conversation?.groupB?.name} <em>replying</em> {conversation?.groupA?.name}
              </>
            )}
      </span>

    </div>

  </div>

  {/* RIGHT SIDE (Share Button) */}
  <button
    onClick={shareChat}
    style={{
      border: "none",
      background: "transparent",
      width: "36px",
      height: "36px",
      borderRadius: "12px",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <img
      src="/share.png"
      alt="copy link"
      style={{ width: "20px", height: "20px" }}
    />
  </button>

</div>

    {/* Chat Messages */}
    <div
      style={{
        flex: 1,
        overflowY: "auto",
        padding: "20px",
        background: "#ece5dd"
      }}
    >
      {combinedMessages.map(m => {
          //const isMine = m.fromGroup === groupId;
          const isWatcher = !groupId;

          let isMine;

          if (isWatcher) {
            // spectators: B on right, A on left
            isMine = m.fromGroup === "B";
          } else {
            // players: their group on right
            isMine = m.fromGroup === groupId;
          }

          const groupName =
            m.fromGroup === "A"
              ? conversation?.groupA?.name
              : conversation?.groupB?.name;

          const groupInitial = groupName?.charAt(0)?.toUpperCase();
          // const isInternal = m.type === "internal";

          return (
            <div
              key={m.id}
              style={{
                display: "flex",
                justifyContent: isMine ? "flex-end" : "flex-start",
                marginBottom: m.type === "official" ? "18px" : "6px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: isMine ? "row-reverse" : "row", 
                  alignItems: "flex-start",
                  gap: "8px",
                  maxWidth: "60%",
                  
                }}
              >
                {/* Avatar */}
                {m.type === "internal" && (
                  m.senderPhoto ? (
                    <img
                      onClick={() => openProfile(m.senderId)}
                      src={m.senderPhoto}
                      alt="profile"
                      style={{
                        width: "26px",
                        height: "26px",
                        borderRadius: "50%",
                        objectFit: "cover",
                        opacity: 0.8,
                        flexShrink: 0,
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        width: "32px",
                        height: "32px",
                        borderRadius: "50%",
                        background: "#6c757d",
                        color: "white",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: "bold",
                        flexShrink: 0,
                      }}
                    >
                      {m.senderName?.charAt(0).toUpperCase()}
                    </div>
                  )
                )}

                {m.type === "official" && (
                  <div
                    style={{
                      width: "32px",
                      height: "32px",
                      borderRadius: "50%",
                      background: m.fromGroup === "A" ? "#6f42c1" : "#0d6efd", // ✅ blue for A, purple for B
                      color: "white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: "bold",
                      flexShrink: 0,
                    }}
                  >
                    {groupInitial}
                  </div>
                )}

                {/* Message Content */}
                <div>
                  {m.type === "internal" && (
                    <div
                      style={{
                        fontSize: "12px",
                        fontWeight: "400",
                        marginBottom: "3px",
                        textAlign: isMine ? "right" : "left", // optional polish
                        display: "flex",
                        gap: "6px",
                        justifyContent: isMine ? "flex-end" : "flex-start",
                        alignItems: "center",
                        color: "#555"
                      }}
                    >
                      {/* <span>
                         {m.senderName}
                      </span> */}

                      
                    </div>
                    
                  )}

                  {/* Message Bubble */}
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: isMine ? "flex-end" : "flex-start",
                    }}
                  >
                    
                    {/* Message Bubble */}
                    <div
                      style={{
                        background:
                          m.type === "internal"
                            ? "#ece5dd"
                            : isMine
                            ? "#c8e8d9"
                            : "#cee6ce",
                        padding: "10px 14px",
                        paddingBottom: isMine && m.type === "official" ? "18px" : "10px",
                        borderRadius:
                          m.type === "internal"
                            ? "5px"
                            : isMine
                            ? "18px 18px 0 18px"
                            : "18px 18px 18px 0",
                        boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
                        maxWidth: "100%",
                        wordBreak: "break-word",
                        whiteSpace: "pre-wrap",
                        fontSize: "13px",
                        position: "relative",
                      }}
                    >
                    <span>{m.content}</span>
                      

                      {/* Read checkmarks INSIDE bubble */}
                      {m.type === "official" && isMine && (
                        <span
                          style={{
                            position: "absolute",
                            bottom: "4px",
                            right: "8px",
                            display: "flex",
                            alignItems: "center",
                            fontSize: "12px",
                            color: "#666",
                            // margin: "8px",
                            fontWeight: "bold",
                            letterSpacing: "-2px",
                            color: m.readBy?.length > 0 ? "#00b2c2" : "#888", // green if read, gray if not
                          }}
                        >
                          {/* first tick */}
                          <span style={{ transform: "scaleX(1)" }}>✓</span>

                          {/* second tick slightly overlapping */}
                          <span
                            style={{
                              marginLeft: "-4px",
                              transform: "scaleX(1)"
                            }}
                          >
                            ✓
                          </span>
                        </span>
                      )}
                    </div>

                    {m.type === "internal" && (
                      <div
                        style={{
                          fontSize: "10px",
                          fontWeight: "600",
                          marginBottom: "12px",
                          color: "#888",
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                          alignSelf: isMine ? "flex-end" : "flex-start",
                          marginTop: "4px",
                        }}
                      >
                      

                        {/* Vote Icon */}
                        <span
                          onClick={() => voteInternalMessage(m)}
                          style={{
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: "2px",
                            userSelect: "none",
                          }}
                        >
                          <span style={{ fontSize: "11px" }}>
                          <img src="/heart.png" alt="vote" style={{ width: "14px", height: "14px"}} />
                          {/* <a href="https://www.flaticon.com/free-icons/send" title="send icons"> </a> */}
                          </span>
                          <span> { m.votes?.length || 0}  </span>  
                        </span>

                        {/* Sender + Time */}
                          <span
                            onClick={() => openProfile(m.senderId)}
                            style={{ cursor: "pointer" }}
                          >
                            @{m.senderName.replace(/\s+/g, "")}
                          </span>
                        <span> {formatTime(m.createdAt)}</span>

                      </div>
                    )}

                    {m.type === "official" && (
                      <div
                        style={{
                          fontSize: "10px",
                          fontWeight: "600",
                          color: "#888",
                          marginTop: "4px",
                          alignSelf: isMine ? "flex-end" : "flex-start",
                        }}
                      >
                        
                          {m.fromGroup === "A"
                            ? conversation?.groupA?.name
                            : conversation?.groupB?.name} , {formatTime(m.createdAt)}

                          {/* {isMine && (
                            <span style={{ marginLeft: "6px", fontSize: "11px" }}>
                              {m.readBy?.length > 0 ? "✓✓" : "✓"}
                            </span>
                          )} */}
                      </div>
                    )}
                  </div>
                </div>
                
              </div>
            </div>
          );
        })}

        {/* 👇 Invisible anchor */}
        <div ref={messagesEndRef} />
      
    </div>


   {conversation?.currentTurn && lastOfficialMessage && groupId && (
  <div
    style={{
      margin: "10px 12px",
      display: "flex",
      justifyContent: "center",
    }}
  >
    <div
      style={{
        maxWidth: "85%",
        width: "100%",
        padding: "12px 14px",
        borderRadius: "16px",
        background: "rgba(255,255,255,0.75)",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        boxShadow: "0 4px 18px rgba(0,0,0,0.08)",
        border: "1px solid rgba(0,0,0,0.05)",
        textAlign: "center",
        transition: "all 0.25s ease",
      }}
    >

      {/* 🔥 Topic (Hero Text) */}
      <div
  style={{
    fontSize: "13px",
    color: "#6b7280",
    marginBottom: "6px",
    textAlign: "center",
  }}
>
  <span style={{ opacity: 0.7 }}>Topic: </span>{" "}
  <span style={{ color: "#1f2937", fontWeight: "500" }}>
    {conversation?.topic}
  </span>
</div>

      {/* Subtle divider */}
      <div
        style={{
          height: "1px",
          width: "40px",
          background: "rgba(0,0,0,0.08)",
          margin: "6px auto 8px",
          borderRadius: "2px",
        }}
      />

      {/* ⏳ Status line */}
      <div
        style={{
          fontSize: "12.5px",
          color: "#6b7280",
          fontWeight: "500",
        }}
      >
        {!isMyTurn ? (
          groupId === "A"
            ? countdown === "00:00"
              ? `${conversation?.groupB?.name} is replying…`
              : `${conversation?.groupB?.name} replies in ${countdown}`
            : countdown === "00:00"
              ? `${conversation?.groupA?.name} is replying…`
              : `${conversation?.groupA?.name} replies in ${countdown}`
        ) : (
          countdown === "00:00"
            ? `Respond to the last chat from ${respondingToGroupName} Group`
            : `Respond to the last chat from ${respondingToGroupName} group in ${countdown}`
        )}
      </div>

    </div>
  </div>
)}


    {/* Single Input */}
{groupId && (
  <div
    style={{
      padding: "10px 14px",
      background: "rgba(255,255,255,0.85)",
      backdropFilter: "blur(12px)",
      borderTop: "1px solid rgba(0,0,0,0.05)",
      position: "sticky",
      bottom: 0,
      zIndex: 10,
    }}
  >
    <div
      style={{
        display: "flex",
        alignItems: "flex-end",
        gap: "10px",
        background: "#f9fafb",
        padding: "8px 10px",
        borderRadius: "20px",
        boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
      }}
    >
      <textarea
        value={text}
        rows={1}
        onChange={(e) => {
          setText(e.target.value);

          // 🔥 Auto-grow
          e.target.style.height = "auto";
          e.target.style.height = e.target.scrollHeight + "px";
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
          }
        }}
        placeholder={
          !groupId
            ? "Watching conversation..."
            : isMyTurn
            ? "Write your reply..."
            : "Waiting for the other group..."
        }
        disabled={!isMyTurn}
        style={{
          flex: 1,
          border: "none",
          outline: "none",
          resize: "none",
          background: "transparent",
          fontSize: "14px",
          lineHeight: "1.4",
          maxHeight: "120px",
          overflowY: "auto",
          color: "#111",
          padding: "6px 4px",
          cursor: !isMyTurn ? "not-allowed" : "text",
          opacity: !isMyTurn ? 0.6 : 1,
        }}
      />

      {/* Send Button */}
      <button
        onClick={sendMessage}
        disabled={!isMyTurn || !text.trim()}
        style={{
          border: "none",
          background:
            isMyTurn && text.trim()
              ? "linear-gradient(135deg,#22c55e,#16a34a)"
              : "#e5e7eb",
          width: "40px",
          height: "40px",
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor:
            !isMyTurn || !text.trim() ? "not-allowed" : "pointer",
          transition: "all 0.2s ease",
          boxShadow:
            isMyTurn && text.trim()
              ? "0 4px 10px rgba(34,197,94,0.3)"
              : "none",
        }}
      >
        <img
          src="/send.png"
          alt="send"
          style={{
            width: "16px",
            height: "16px",
            filter:
              isMyTurn && text.trim()
                ? "invert(1)"
                : "invert(0.5)",
          }}
        />
      </button>
    </div>
  </div>
)}



   <Modal
      show={showProfileModal}
      onHide={() => setShowProfileModal(false)}
      centered
    >
      <Modal.Body
        style={{
          padding: "32px",
          textAlign: "center",
          borderRadius: "18px",
          position: "relative",
        }}
      >

        <button
          onClick={() => setShowProfileModal(false)}
          style={{
            position: "absolute",
            top: "14px",
            right: "14px",
            border: "none",
            background: "transparent",
            fontSize: "20px",
            cursor: "pointer",
            color: "#6b7280",
            lineHeight: "1",
          }}
        >
          ✕
        </button>

        {/* Profile Photo */}
        <div style={{ marginBottom: "14px" }}>
          {profileUser?.photo ? (
            <img
              src={profileUser.photo}
              alt="profile"
              style={{
                width: "90px",
                height: "90px",
                borderRadius: "50%",
                objectFit: "cover",
                boxShadow: "0 8px 20px rgba(0,0,0,0.12)"
              }}
            />
          ) : (
            <div
              style={{
                width: "90px",
                height: "90px",
                borderRadius: "50%",
                background: "#1e293b",
                margin: "0 auto",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "white",
                fontSize: "34px",
                fontWeight: "600",
                boxShadow: "0 8px 20px rgba(0,0,0,0.12)"
              }}
            >
              {profileUser?.name?.charAt(0)?.toUpperCase()}
            </div>
          )}
        </div>

        {/* Name */}
        <h5
          style={{
            fontWeight: 600,
            marginBottom: "20px",
            color: "#6b7280",
          }}
        >
          @{profileUser?.name.replace(/\s+/g, "")}
        </h5>

       

        {/* Stats */}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: "12px",
            marginBottom: "20px"
          }}
        >
          <div
            style={{
              background: "#f8fafc",
              padding: "12px 18px",
              borderRadius: "12px",
              minWidth: "90px"
            }}
          >
            <div style={{ fontWeight: 600, fontSize: "18px" }}>
              {profileUser?.groupsStarted || 0}
            </div>
            <div style={{ fontSize: "12px", color: "#6b7280" }}>
              Start
            </div>
          </div>

          <div
            style={{
              background: "#f8fafc",
              padding: "12px 18px",
              borderRadius: "12px",
              minWidth: "90px"
            }}
          >
            <div style={{ fontWeight: 600, fontSize: "18px" }}>
              {profileUser?.totalVotes || 0}
            </div>
            <div style={{ fontSize: "12px", color: "#6b7280" }}>
              Likes
            </div>
          </div>

          <div
            style={{
              background: "#f8fafc",
              padding: "12px 18px",
              borderRadius: "12px",
              minWidth: "90px"
            }}
          >
            <div style={{ fontWeight: 600, fontSize: "18px" }}>
              {profileUser?.officialMessages || 0}
            </div>
            <div style={{ fontSize: "12px", color: "#6b7280" }}>
              Influence
            </div>
          </div>

        </div>

        {/* Groups */}
        {profileUser?.groups?.length > 0 && (
          <div style={{ marginBottom: "20px" }}>
            <div
              style={{
                fontSize: "12px",
                fontWeight: 600,
                color: "#6b7280",
                marginBottom: "8px",
                letterSpacing: "0.5px"
              }}
            >
              MEMBER OF
            </div>

            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "center",
                gap: "8px"
              }}
            >
              {profileUser.groups.map((g, i) => (
                <span
                  key={i}
                  style={{
                    background: "#eef2ff",
                    color: "#4338ca",
                    padding: "6px 12px",
                    borderRadius: "999px",
                    fontSize: "12px",
                    fontWeight: 500
                  }}
                >
                  {g.groupName}
                </span>
              ))}
            </div>
          </div>
        )}


      </Modal.Body>
    </Modal>



    <Modal show={showJoinModal} backdrop="static" centered>
      <Modal.Body style={{ padding: "32px", textAlign: "center" }}>

        {/* STEP 1 — CHOOSE GROUP */}
        {joinStep === "group" && (
          <>
            <h4 className="fw-bold mb-2">
              Join the conversation
            </h4>

            <p style={{ color: "#666", fontSize: "14px", marginBottom: "22px" }}>
              Choose which side you identify with.
            </p>
            

            <div style={{ display: "grid", gap: "10px" }}>

              <div
                onClick={() => {
                  setSelectedGroup("A");
                  setUsername(generateUsername());
                  setJoinStep("login");
                }}
                style={{
                  padding: "18px",
                  borderRadius: "14px",
                  border: "1px solid #eee",
                  cursor: "pointer",
                  transition: "all 0.2s",
                  fontWeight: "600",
                }}
              >
                {conversation?.groupA?.name} ( 100% access )
              </div>

              <div
                onClick={() => {
                  setSelectedGroup("B");
                  setUsername(generateUsername());
                  setJoinStep("login");
                }}
                style={{
                  padding: "18px",
                  borderRadius: "14px",
                  border: "1px solid #eee",
                  cursor: "pointer",
                  transition: "all 0.2s",
                  fontWeight: "600",
                }}
              >
                {conversation?.groupB?.name} ( 100% access )
              </div>

              <div
                onClick={() => {
                  setSelectedGroup("");
                  setUsername(generateUsername());
                  setJoinStep("login");
                }}
                style={{
                  padding: "18px",
                  borderRadius: "14px",
                  border: "1px solid #eee",
                  color: "#666",
                  cursor: "pointer",
                  fontSize: "14px",
                  marginBottom: "22px"
                }}
              >
                Watch only ( invisible potential replies )
              </div>

              <p
                className="text-center"
                style={{ fontSize: "12px", color: "#047320",  marginBottom: "0px"  }}
              >
                Switching groups will not be possible after joining.
              </p>
            </div>
            
          </>
        )}

    {/* STEP 2 — SIGN IN */}
      {joinStep === "login" && (
        <>
          {/* <h5 className="fw-bold mb-2">
            Continue as {selectedGroup === "A"
              ? conversation?.groupA?.name
              : conversation?.groupB?.name}
          </h5> */}

          {/* <p style={{ color: "#666", fontSize: "14px", marginBottom: "20px" }}>
            Sign in to join the conversation.
          </p> */}

            <>
              <p style={{ color: "#666", fontSize: "14px", marginBottom: "14px" }}>
                 We have chosen this username for you but you can edit it.
              </p>

              {/* USERNAME FIELD */}
              <div style={{ display: "flex", gap: "8px", marginBottom: "18px" }}>
                
                <input
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="form-control"
                  placeholder="@username"
                  style={{
                    borderRadius: "12px",
                    textAlign: "center",
                    fontWeight: "600"
                  }}
                />

                {/* RANDOM BUTTON */}
                {/* <button
                  onClick={() => setUsername(generateUsername())}
                  style={{
                    border: "none",
                    background: "#f3f4f6",
                    borderRadius: "12px",
                    padding: "0 12px",
                    cursor: "pointer",
                    fontSize: "18px"
                  }}
                >
                  🔄
                </button> */}

              </div>

            </>

          <Button
            disabled={!username.trim()}
            onClick={async () => {

              const result = await signInWithPopup(auth, provider);
              const user = result.user;

              const userRef = doc(db, "users", user.uid);

              const userSnap = await getDoc(userRef);
              const userConversationRef = doc(
                    db,
                    "userConversations",
                    `${user.uid}_${conversationId}`
                  );


              if (!userSnap.exists()) {
                await setDoc(userRef, {
                  username: username.replace(/\s+/g, ""),
                  photoURL: user.photoURL,
                  email: user.email,
                  createdAt: serverTimestamp(),
                  groupsJoined: [
                    {
                      conversationId,
                      groupId: selectedGroup
                    }
                  ]
                });
              } else {

                await updateDoc(userRef, {
                  groupsJoined: arrayUnion({
                    conversationId,
                    groupId: selectedGroup
                  })
                });

              }

               // Save membership
              await setDoc(userConversationRef, {
                userId: user.uid,
                conversationId: conversationId,
                group: selectedGroup,
                joinedAt: new Date()
              });

              navigate(`/chat/${conversationId}?group=${selectedGroup}`);
              setShowJoinModal(false);

            }}
            className="rounded-pill w-100"
            style={{
              background: "linear-gradient(135deg,#2d512f,#318333)",
              border: "none"
            }}
          >
            Continue with Google
          </Button>
        </>
      )}

    </Modal.Body>
  </Modal>



    <Modal show={showStartModal} backdrop="static" centered>
  <Modal.Body style={{ padding: "30px", textAlign: "center" }}>

    {/* STEP 1 — INVITE */}
    {startStep === "invite" && (
      <>
        <div style={{ fontSize: "36px", marginBottom: "10px" }}>🚀</div>

        <h5 className="fw-bold mb-2">
          Chat is ready!
        </h5>

        <p style={{ fontSize: "14px", color: "#666", marginBottom: "20px" }}>
          Invite friends to represent each group and start the discussion.
        </p>

        <Button
          onClick={shareChat}
          className="rounded-pill w-100"
          style={{
            background: "linear-gradient(135deg,#2d512f,#318333)",
            border: "none"
          }}
        >
          <img
            src="/share.png"
            alt="share"
            style={{ width: "18px", height: "18px", filter: "invert(1)" }}
          />
        </Button>
      </>
    )}

    {/* STEP 2 — FIRST MESSAGE */}
    {startStep === "message" && (
      <>
        <div style={{ fontSize: "36px", marginBottom: "10px" }}>
          👍
        </div>

        <h5 className="fw-bold mb-2">
          Invite sent
        </h5>

        <p style={{ fontSize: "14px", color: "#666", marginBottom: "18px" }}>
          Now write the first message to kick-start the discussion.
        </p>

        <textarea
          className="form-control"
          rows={3}
          placeholder="Why did you start this conversation?"
          value={text}
          onChange={(e) => setText(e.target.value)}
          style={{ marginBottom: "14px" }}
        />

        <Button
          onClick={sendKickstartMessage}
          className="rounded-pill w-100"
          style={{
            background: "linear-gradient(135deg,#2d512f,#318333)",
            border: "none"
          }}
        >
          <img
            src="/send.png"
            alt="send"
            style={{ width: "16px", height: "16px", filter: "invert(1)" }}
          />
        </Button>
      </>
    )}

  </Modal.Body>
</Modal>

    <Modal
      show={showInternalInfoModal}
      onHide={closeInternalInfoModal}
      centered
    >
      <Modal.Body
        style={{
          padding: "28px",
          borderRadius: "18px"
        }}
      >

      <div style={{ textAlign: "center", marginBottom: "16px" }}>
        <div style={{ fontSize: "32px" }}>💬</div>

        {/* <h5
          style={{
            fontWeight: 700,
            marginTop: "6px",
            marginBottom: "6px"
          }}
        >
          How Group Discussions Work
        </h5> */}

        <p style={{ fontSize: "14px", color: "#666" }}>
          Messages on the right with a like icon are <b>potential group replies</b>.
          Only members of your group can see them.
        </p>
      </div>


      {/* INTERNAL MESSAGE PREVIEW */}
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginBottom: "14px"
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "row-reverse",
            alignItems: "flex-start",
            gap: "8px",
            maxWidth: "70%"
          }}
        >

          {/* Avatar */}
          <img
            src="https://i.pravatar.cc/40"
            alt=""
            style={{
              width: "26px",
              height: "26px",
              borderRadius: "50%",
              opacity: 0.8
            }}
          />

          <div>

            {/* Bubble */}
            <div
              style={{
                background: "#fbfafa",
                padding: "10px 14px",
                borderRadius: "6px",
                fontSize: "13px",
                boxShadow: "0 1px 2px rgba(0,0,0,0.08)"
              }}
            >
              This is an example of a potential reply. The reply with the highest vote becomes official.
            </div>

            {/* Footer */}
            <div
              style={{
                fontSize: "10px",
                fontWeight: "600",
                color: "#888",
                marginTop: "4px",
                display: "flex",
                gap: "6px",
                alignItems: "center",
                justifyContent: "flex-end"
              }}
            >
              <div>
                <img
                  src="/heart.png"
                  alt=""
                  style={{ width: "14px", height: "14px" }}
                />

                <span> 11</span>
              </div>
            

              <span>@CrazyFox</span>

              <span>12:21</span>
            </div>

          </div>
        </div>
      </div>


      <div
        style={{
          background: "#f8fafc",
          borderRadius: "10px",
          padding: "12px",
          fontSize: "13px",
          color: "#555",
          marginBottom: "18px"
        }}
      >
        👍 The potential reply from your group that receives the <b>most likes</b> becomes your group's
        <b> official reply</b> sent to the other group.
      </div>


      {/* Don't show again */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          marginBottom: "16px"
        }}
      >
        <input
          type="checkbox"
          checked={dontShowAgain}
          onChange={() => setDontShowAgain(!dontShowAgain)}
        />

        <span style={{ fontSize: "12px", color: "#555" }}>
          Don't show this again
        </span>
      </div>


      <Button
        onClick={closeInternalInfoModal}
        className="w-100 rounded-pill"
        style={{
          background: "linear-gradient(135deg,#2d512f,#318333)",
          border: "none"
        }}
      >
        Got it
      </Button>

      </Modal.Body>
    </Modal>

    {/* Internal Chat Visibility Toggle */}
    {auth.currentUser && groupId && (
    <div
      onClick={() => setShowInternalChats(!showInternalChats)}
      style={{
        position: "fixed",
        bottom: "170px",
        right: "18px",
        zIndex: 20,
        background: "white",
        borderRadius: "999px",
        padding: "8px 12px",
        display: "flex",
        alignItems: "center",
        gap: "8px",
        boxShadow: "0 8px 25px rgba(0,0,0,0.15)",
        cursor: "pointer",
        userSelect: "none",
        fontSize: "12px",
        fontWeight: "600",
        color: "#333",
        transition: "all .2s ease"
      }}
    >
      <span style={{ fontSize: "16px" }}>
        {showInternalChats ? "👁️" : "🙈"}
      </span>

      <span>
        {showInternalChats ? "Potential Replies ON" : "Potential Replies OFF"}
      </span>

      <div
        style={{
          width: "34px",
          height: "18px",
          background: showInternalChats ? "#22c55e" : "#d1d5db",
          borderRadius: "999px",
          position: "relative",
          transition: "all .2s ease"
        }}
      >
        <div
          style={{
            width: "14px",
            height: "14px",
            background: "white",
            borderRadius: "50%",
            position: "absolute",
            top: "2px",
            left: showInternalChats ? "18px" : "2px",
            transition: "all .2s ease",
            boxShadow: "0 1px 3px rgba(0,0,0,0.25)"
          }}
        />
      </div>
    </div>
    )}

  </div>  
  
);
}